import React, { useContext, useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

import FieldTitle from './FieldTitle';
import FieldDecorators from './FieldDecorators';

import Tooltip from '../../misc/Tooltip';
import Icon from '../../misc/Icon';
import LoadingIndicator from '../../misc/LoadingIndicator';
import { ActionToolbar, ImageManagerDropzone } from '../../misc/ImageManager';
import { isEmptyValue } from '../../../lib/validation';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import faExclamationTriangle from '../../../theme/fortawesomeIcons/free-solid-svg-icons/faExclamationTriangle';
import { Red } from '../../../constants/Colors';
import FieldTypes from '../../../constants/FieldTypes';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}

  /*
   * Only use height when you don't need height 
   * measurements of the table field.
   */
  ${(props) => {
    return props.measure ? '' : 'height: 100%;';
  }}

  #image-manager-action-toolbar {
    display: ${(props) => (!props.autoShow ? 'none' : 'block')};
  }

  &:hover {
    #image-manager-action-toolbar {
      display: block;
      svg{
        margin-left: 0px;
        margin-right: 0px;
      }
    }
  }

`;

const StyledTooltipWrapper = styled.div`
  position: absolute;
  right:0;
  top:0;
  z-index:99
`;

const StyleFileViewer = styled.span`
  display: flex;
  max-width: 200px;
  padding: 10px 4px;
  align-items: center;
  border: ${(props) => (props.border ? props.border : '')};
  border-radius: ${(props) => (props.borderRadius ? `${props.borderRadius}px` : '')};
  margin: ${(props) => (props.padding ? `${props.padding}px` : '0px')};
  
  
  svg{
    margin-left: 4px;
    margin-right: 4px;
  }

  #image-manager-action-toolbar {
    svg{
      margin-left: 0px;
      margin-right: 0px;
    }
  }

  &:hover {
    cursor: pointer;
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  }
`;

const StyledSpan = styled.span`
  margin-left: 5px;
  margin-right: 5px;
  margin-right: 8px;
  white-space: nowrap;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
`;

const StyledFileViewer = styled.div`
  display:flex;
  flex-wrap: wrap;
  ${(props) => {

    if (!props.multiple) {
      return '';
    } else {
      return `  
        scrollbar-gutter: stable;
        margin-right: -21px;
        overflow-y: auto;
      `;
    }

  }}
`;

const StyledLoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

export const FadeAnimation = keyframes`
  0% {
    opacity: 1;
    visibility: visible;
  }
  100% {
    opacity: 0;
    visibility: hidden;
  }
`;

const StyledFailureWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  animation: ${FadeAnimation} 1s ease-in 0s forwards;
`;

const StyledImageManagerWrapper = styled.div`
  height: 100%;
  overflow: hidden;
  border: 1px solid transparent;

`;

const StyledFieldHeader = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 6px;
  padding-bottom: 4px;
  padding-right: 20px;
`;

const FileField = ({

  fieldId,
  fieldIdentifier,
  fieldPositionId,

  title,
  value,
  multi,
  config,

  fontColor,
  margin,
  padding,
  borderRadius,
  borderWidth,
  borderColor,
  backgroundColor,
  primaryColor,
  secondaryColor,

  //Title styles
  titleDisplay,
  titleFontSize,
  titleFontColor,
  titleTextAlign,
  titleFontStyle,
  titleFontWeight,
  titleTextTransform,
  titleTextDecoration,

  disabled,
  isMobileView,
  enableSystemFilePicker,

  onFileUpload,
  onFileClick,
  onFileDelete,
  onChange,
  onFocus,

  tipTitle,
  tipDescription,
  tipVisible,

  //Used for calculating width and height of element
  measure,
  measuredHeight,
  measuredOn,
  updatedOn,
  onMeasureCompleted,

  type,
  decorators,
  onDecoratorClick,
  fieldMode,

}) => {

  const theme = useContext(ThemeContext);

  const [wrapperRef, setWrapperRef] = useState(null);
  const [loadedFiles, setLoadedFiles] = useState(value && value.length);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Important Note: Immediately fire onLoadCompleted for placeholder because
   * there are no files loading to wait for. This is very important for auto 
   * measuring and PDF rendering.
   */
  useEffect(() => {
    if (!value || value.length < 1) setLoadedFiles(true);
  }, [value]);

  /**
   * Handle Field Measurement
   */
  const remeasureRequired = !measuredHeight || (measuredOn && updatedOn && measuredOn < updatedOn);
  const hasPopulatedValue = !isEmptyValue(value, FieldTypes.file);
  const refHeightAvailable = wrapperRef && wrapperRef.getBoundingClientRect() && wrapperRef.getBoundingClientRect().height;
  
  useEffect(() => {
    
    if (onMeasureCompleted && wrapperRef && loadedFiles && multi && hasPopulatedValue && measure && refHeightAvailable && (remeasureRequired || !measuredHeight)) {
      requestAnimationFrame(() => {
        onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, {height: refHeightAvailable, measuredOn: new Date().getTime()});
      });
    }

  }, [wrapperRef, loadedFiles, remeasureRequired, measure, value, refHeightAvailable]);


  const handleUpload = (files) => {

    if (onFileUpload) {

      onFileUpload(fieldId, fieldIdentifier, fieldPositionId, files, (loading) => {
        setLoading(loading);
      }, (error) => {
        setError(error);
        setLoading(false);
        setTimeout(() => setError(null), 1000);
      });

    }

  };

  const handleDelete = (i, fileObject) => {

    const nextFileValues = [...value];
    nextFileValues.splice(i, 1);

    if (onFocus) onFocus(fieldId, fieldIdentifier, fieldPositionId, {});
    if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, nextFileValues);

    /**
     * Important Note: onFileDelete is simply to notify about the deletion event. The actual
     * removal of the file is handled by onChange.
     */
    if (onFileDelete) onFileDelete(fieldId, fieldIdentifier, fieldPositionId, fileObject, i);

  };

  const handleFocus = () => {
    if (onFocus) onFocus(fieldId, fieldIdentifier, fieldPositionId, {});
  };

  const handlePreview = (i, fileObject) => {

    if (onFocus) onFocus(fieldId, fieldIdentifier, fieldPositionId, {});

    /**
     * We check if onFileClick is provided in the config because 
     * that truly determines how the file should be handled when clicked. 
     * If it is provided, we call it through the SDK the proper onFileClick.
     * If not, we simply open the file in a new tab via browser.
     */
    if (config?.onFileClick) onFileClick(fieldId, fieldIdentifier, fieldPositionId, fileObject, i);
    else window.open(fileObject.url, '_blank');

  };

  const handleLoadCompleted = () => {
    setLoadedFiles(true);
  };

  const filePillNode = !value ? null : value.map((item, i) => {

    const border = `${borderWidth}px solid ${borderColor}`;

    return (
      <StyleFileViewer
        key={item._id}
        border={border}
        borderRadius={borderRadius}
        padding={padding}
        autoShow={isMobileView}
      >
        <StyledSpan>
          {' '}
          {item.fileName}
          {' '}
        </StyledSpan>
        {item && item.url ? (<Icon icon={ThemeIcons.misc.preview} onClick={() => handlePreview(i, item)} />) : null}
        {!disabled ? (<Icon icon={ThemeIcons.settings.delete} color={theme.dangerColor} onClick={() => handleDelete(i, item)} />) : null}
        <ActionToolbar
          {...{
            type,
            multiple: multi,
            onUpload: handleUpload,
            onFocus: handleFocus,
            enableSystemFilePicker,
            disabled,
            isMobileView,
            ...theme
          }}
        />
      </StyleFileViewer>

    );
  });

  const LoadingIndicatorNode = loading && !error ? (
    <StyledLoadingWrapper>
      <LoadingIndicator size="lg" color={theme.primaryColor} />
    </StyledLoadingWrapper>
  ) : null;

  const ErrorNode = error ? (
    <StyledFailureWrapper>
      <Icon
        icon={faExclamationTriangle}
        color={Red}
        size="lg"
      />
    </StyledFailureWrapper>
  ) : null;

  return (
    <StyledWrapper
      {...{
        margin,
        measure: measure && hasPopulatedValue && multi
      }}
      autoShow={isMobileView}
      ref={(e) => {
        if (e && !wrapperRef) setWrapperRef(e);
      }}
    >
      <StyledFieldHeader>
        <FieldTitle
          {...{
            title,
            titleDisplay,
            titleFontSize,
            titleFontColor,
            titleTextAlign,
            titleFontStyle,
            titleFontWeight,
            titleTextTransform,
            titleTextDecoration,
          }}
        />
        <FieldDecorators
          decorators={decorators}
          fieldMode={fieldMode}
          fieldId={fieldId}
          fieldIdentifier={fieldIdentifier}
          fieldPositionId={fieldPositionId}
          onDecoratorClick={onDecoratorClick}
        />
      </StyledFieldHeader>
      {disabled ? null : (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      )}
      {
        ((!value || (value && value.length < 1)) && !loading && !error) ? (
          <StyledImageManagerWrapper>
            <ImageManagerDropzone
              {...{
                type,
                disabled,
                multiple: multi,
                enableSystemFilePicker,
                onUpload: handleUpload,
                onFocus: handleFocus,
                onLoadCompleted: handleLoadCompleted,

                fontColor,
                borderColor,
                borderWidth,
                borderRadius,
                backgroundColor,
                primaryColor,
                secondaryColor

              }}
            />
          </StyledImageManagerWrapper>
        ) : null
      }
      {LoadingIndicatorNode}
      {ErrorNode}
      {!loading && !error ? (
        <StyledFileViewer
          multiple={multi}
        >
          {filePillNode}
        </StyledFileViewer>
      ) : null}

    </StyledWrapper>
  );

};

export default React.memo(FileField);
